import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Globe, Link2, Loader2, Trash2, X } from "lucide-react";
import api from "@/lib/api";

const EXPIRY_OPTS = [
    { days: 30, label: "30 dagen" },
    { days: 90, label: "90 dagen" },
    { days: 365, label: "1 jaar" },
];

/** Eén publieke deellink voor het volledige project (video + reel + fotoreeks). */
export default function ProjectShareButton({ req, onUpdated }) {
    const [open, setOpen] = useState(false);
    const [days, setDays] = useState(30);
    const [allowDownload, setAllowDownload] = useState(true);
    const [busy, setBusy] = useState(false);
    const token = req?.project_share_token;
    const publicUrl = token ? `${window.location.origin}/api/project-meta/${token}` : "";
    const hasAssets = !!(req?.final_video_url || req?.reel_url || (req?.ai_photo_urls || []).length);

    const create = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/requests/${req.id}/project-share-link`, {
                expires_in_days: days, allow_download: allowDownload,
            });
            onUpdated?.({ ...req, project_share_token: data.token, project_share_expires_at: data.expires_at, project_share_allow_download: data.allow_download });
            toast.success("Totaalproject-deellink aangemaakt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aanmaken mislukt");
        }
        setBusy(false);
    };

    const revoke = async () => {
        setBusy(true);
        try {
            await api.delete(`/requests/${req.id}/project-share-link`);
            onUpdated?.({ ...req, project_share_token: null, project_share_expires_at: null });
            toast.success("Deellink ingetrokken");
        } catch { toast.error("Intrekken mislukt"); }
        setBusy(false);
    };

    const copy = async () => {
        try { await navigator.clipboard.writeText(publicUrl); toast.success("Link gekopieerd"); }
        catch { toast.error("Kopiëren mislukt"); }
    };

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="project-share-btn"
                className={`inline-flex items-center gap-2 h-8 px-3 rounded-sm text-xs font-bold border transition active:scale-[0.98] ${token
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : "border-[#E5A93D]/50 bg-[#E5A93D]/10 text-zinc-900 hover:bg-[#E5A93D]/20"}`}
            >
                <Globe className="h-3.5 w-3.5 text-[#E5A93D]" />
                {token ? "Deellink totaalproject actief" : "Deellink totaalproject"}
            </button>
            {open && (
                <div className="absolute z-40 mt-2 left-0 w-[340px] p-4 rounded-sm border border-zinc-200 bg-white shadow-xl" data-testid="project-share-popover">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-bold flex items-center gap-2"><Link2 className="h-4 w-4 text-[#E5A93D]" /> Totaalproject delen</p>
                        <button type="button" onClick={() => setOpen(false)} data-testid="project-share-close"><X className="h-4 w-4 text-zinc-400" /></button>
                    </div>
                    <p className="text-[11px] text-zinc-500 mb-3">Eén publieke pagina met de video, reel en fotoreeks — incl. opmerkingen per seconde en per foto.</p>
                    {!hasAssets && <p className="text-xs text-red-600 mb-2">Nog geen afgewerkte video, reel of foto's beschikbaar.</p>}
                    {!token ? (
                        <>
                            <div className="flex gap-1.5 mb-3">
                                {EXPIRY_OPTS.map((o) => (
                                    <button key={o.days} type="button" onClick={() => setDays(o.days)}
                                        data-testid={`project-share-days-${o.days}`}
                                        className={`flex-1 h-8 rounded-sm text-xs font-bold border transition ${days === o.days ? "border-[#E5A93D] bg-[#E5A93D]/15 text-zinc-950" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            <label className="flex items-center gap-2 text-xs text-zinc-700 mb-3 cursor-pointer">
                                <input type="checkbox" checked={allowDownload} onChange={(e) => setAllowDownload(e.target.checked)}
                                    data-testid="project-share-download-toggle" className="h-4 w-4" style={{ accentColor: "#E5A93D" }} />
                                Downloaden toestaan (video, reel & foto-ZIP)
                            </label>
                            <button type="button" onClick={create} disabled={busy || !hasAssets} data-testid="project-share-create"
                                className="w-full h-9 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black text-sm font-bold transition disabled:opacity-50 inline-flex items-center justify-center gap-2">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Maak publieke deellink
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-1.5 mb-2">
                                <input readOnly value={publicUrl} data-testid="project-share-link-input"
                                    className="flex-1 h-8 px-2 rounded-sm border border-zinc-200 bg-zinc-50 text-[11px] text-zinc-700" />
                                <button type="button" onClick={copy} data-testid="project-share-copy"
                                    className="h-8 px-2.5 rounded-sm border border-zinc-200 hover:border-[#E5A93D]/60 transition">
                                    <Copy className="h-3.5 w-3.5 text-zinc-600" />
                                </button>
                            </div>
                            <p className="text-[11px] text-zinc-400 mb-3">
                                {req.project_share_expires_at ? `Verloopt: ${new Date(req.project_share_expires_at).toLocaleDateString("nl-BE")}` : "Geen vervaldatum"}
                                {" · "}Download {req.project_share_allow_download === false ? "uit" : "aan"}
                            </p>
                            <button type="button" onClick={revoke} disabled={busy} data-testid="project-share-revoke"
                                className="w-full h-8 rounded-sm border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 transition inline-flex items-center justify-center gap-2">
                                <Trash2 className="h-3.5 w-3.5" /> Link intrekken
                            </button>
                            <p className="mt-2 text-[10px] text-zinc-400">De aparte deellinks per video/reel/foto's blijven apart werken.</p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
